import { useState, useEffect } from 'react';
import { adminService, SystemConfigDto } from '../services/adminService';
import { publicSystemConfigService } from '../services/publicSystemConfigService';

export interface UseSystemConfigReturn {
    systemConfig: SystemConfigDto | null;
    loading: boolean;
    error: string | null;
    refreshConfig: () => Promise<void>;
}

// Global cache to prevent multiple API calls
let globalSystemConfig: SystemConfigDto | null = null;
let globalConfigPromise: Promise<SystemConfigDto> | null = null;
let lastConfigLoad = 0;
const CONFIG_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
let sessionInactivityInitialized = false;

/** Apply Token Expiration from system configuration to the isolated session policy. */
export async function applySessionInactivityFromConfig(config: SystemConfigDto): Promise<void> {
    try {
        const { applySecurityTokenExpiration, startSessionExpirationPolicy } = await import('../utils/sessionExpirationPolicy');
        applySecurityTokenExpiration(config?.securitySettings ?? config);
        startSessionExpirationPolicy();

        const { sessionInactivityTracker } = await import('../utils/sessionInactivityTracker');
        const sessionTimeoutMinutes = config.securitySettings.sessionTimeoutMinutes;
        sessionInactivityTracker.setSessionTimeout(sessionTimeoutMinutes);

        if (!sessionInactivityInitialized) {
            sessionInactivityInitialized = true;
            sessionInactivityTracker.startPeriodicCheck(() => {}, 1);
        }
    } catch (error) {
        console.warn('useSystemConfig: Failed to initialize session expiration policy:', error);
    }
}

export const useSystemConfig = (): UseSystemConfigReturn => {
    const [systemConfig, setSystemConfig] = useState<SystemConfigDto | null>(globalSystemConfig);
    const [loading, setLoading] = useState(!globalSystemConfig);
    const [error, setError] = useState<string | null>(null);

    const loadSystemConfig = async () => {
        // Check if we have a cached config that's still valid
        const now = Date.now();
        if (globalSystemConfig && (now - lastConfigLoad) < CONFIG_CACHE_DURATION) {
            console.log('useSystemConfig: Using cached config');
            setSystemConfig(globalSystemConfig);
            setLoading(false);
            return;
        }

        // If there's already a config loading, wait for it
        if (globalConfigPromise) {
            console.log('useSystemConfig: Waiting for existing config load');
            try {
                const config = await globalConfigPromise;
                setSystemConfig(config);
                setLoading(false);
                return;
            } catch (err) {
                // Continue with new load if existing one failed
            }
        }

        try {
            setLoading(true);
            setError(null);

            // Create a promise for this config load
            globalConfigPromise = (async () => {
                let config: any = null;
                let sessionPolicy = await publicSystemConfigService.getSessionPolicy();

            // Only hit admin settings when an admin session exists (avoids learner 401 noise)
            try {
                if (adminService.isAdminAuthenticated()) {
                    config = await adminService.getSystemSettings();
                    console.log('useSystemConfig: Successfully loaded admin settings:', config);
                }
            } catch (adminError) {
                console.log('useSystemConfig: Admin settings not accessible, trying public config endpoint');

                // Try public system config endpoint (available to all authenticated users)
                try {
                    const publicConfig = await publicSystemConfigService.getPublicConfig();
                    console.log('useSystemConfig: Successfully loaded public config:', publicConfig);
                    sessionPolicy = {
                        sessionTimeoutMinutes: publicConfig.securitySettings?.sessionTimeoutMinutes ?? sessionPolicy.sessionTimeoutMinutes,
                        tokenExpirationMinutes: publicConfig.securitySettings?.tokenExpirationMinutes ?? sessionPolicy.tokenExpirationMinutes,
                        maxLoginAttempts: publicConfig.securitySettings?.maxLoginAttempts ?? sessionPolicy.maxLoginAttempts,
                    };

                    // Convert PublicSystemConfig to the format expected by the rest of the code
                    config = {
                        apiBaseUrl: publicConfig.apiSettings?.baseUrl,
                        apiTimeout: publicConfig.apiSettings?.timeout,
                        apiRetryAttempts: publicConfig.apiSettings?.retryAttempts,
                        enableCaching: publicConfig.apiSettings?.enableCaching,
                        cacheTimeout: publicConfig.apiSettings?.cacheTimeout,
                        maxVocabularyPerLevel: publicConfig.contentSettings?.maxVocabularyPerLevel,
                        maxGrammarLessonsPerLevel: publicConfig.contentSettings?.maxGrammarLessonsPerLevel,
                        maxDialoguesPerLevel: publicConfig.contentSettings?.maxDialoguesPerLevel,
                        maxQuizzesPerLevel: publicConfig.contentSettings?.maxQuizzesPerLevel,
                        defaultQuizTimeLimit: publicConfig.contentSettings?.defaultQuizTimeLimit,
                        enableAudioFeatures: publicConfig.contentSettings?.enableAudioFeatures,
                        defaultFont: publicConfig.persianSettings?.defaultFont,
                        enableRTL: publicConfig.persianSettings?.enableRTL,
                        enableBidirectionalText: publicConfig.persianSettings?.enableBidirectionalText,
                        persianNumberFormatting: publicConfig.persianSettings?.persianNumberFormatting,
                        securitySettings: {
                            sessionTimeoutMinutes: sessionPolicy.sessionTimeoutMinutes,
                            tokenExpirationMinutes: sessionPolicy.tokenExpirationMinutes,
                            maxLoginAttempts: sessionPolicy.maxLoginAttempts ?? 5,
                        }
                    };
                } catch (publicError) {
                    console.log('useSystemConfig: Public config also not accessible, using session-policy defaults');
                }
            }

            if (!config) {
                config = {
                    securitySettings: {
                        sessionTimeoutMinutes: sessionPolicy.sessionTimeoutMinutes,
                        tokenExpirationMinutes: sessionPolicy.tokenExpirationMinutes,
                        maxLoginAttempts: sessionPolicy.maxLoginAttempts ?? 5,
                    }
                };
            }

            // Convert SystemSettings to SystemConfigDto format (use defaults if no config loaded)
            const systemConfigDto: SystemConfigDto = {
                apiSettings: {
                    baseUrl: config?.apiBaseUrl || import.meta.env.VITE_API_BASE_URL || 'http://localhost:5127',
                    timeout: config?.apiTimeout || 10000,
                    retryAttempts: config?.apiRetryAttempts || 3,
                    enableCaching: config?.enableCaching || true,
                    cacheTimeout: config?.cacheTimeout || 300000
                },
                contentSettings: {
                    maxVocabularyPerLevel: config?.maxVocabularyPerLevel || 50,
                    maxGrammarLessonsPerLevel: config?.maxGrammarLessonsPerLevel || 20,
                    maxDialoguesPerLevel: config?.maxDialoguesPerLevel || 15,
                    maxQuizzesPerLevel: config?.maxQuizzesPerLevel || 10,
                    defaultQuizTimeLimit: config?.defaultQuizTimeLimit || 300,
                    enableAudioFeatures: config?.enableAudioFeatures || true
                },
                persianSettings: {
                    defaultFont: config?.defaultFont || 'Vazirmatn',
                    enableRTL: config?.enableRTL || true,
                    enableBidirectionalText: config?.enableBidirectionalText || true,
                    persianNumberFormatting: config?.persianNumberFormatting || true
                },
                securitySettings: {
                    tokenExpirationMinutes: config?.securitySettings?.tokenExpirationMinutes
                        ?? config?.securitySettings?.TokenExpirationMinutes
                        ?? config?.tokenExpirationMinutes
                        ?? config?.TokenExpirationMinutes
                        ?? sessionPolicy.tokenExpirationMinutes
                        ?? 60,
                    sessionTimeoutMinutes: config?.securitySettings?.sessionTimeoutMinutes || config?.sessionTimeoutMinutes || sessionPolicy.sessionTimeoutMinutes || 60,
                    maxLoginAttempts: config?.securitySettings?.maxLoginAttempts || config?.maxLoginAttempts || sessionPolicy.maxLoginAttempts || 5
                }
            };

            console.log('useSystemConfig: Final system config:', systemConfigDto);

            // Cache the config globally
            globalSystemConfig = systemConfigDto;
            lastConfigLoad = Date.now();
            
            return systemConfigDto;
            })();

            const loadedConfig = await globalConfigPromise;
            setSystemConfig(loadedConfig);
            await applySessionInactivityFromConfig(loadedConfig);
        } catch (err) {
            console.error('Failed to load system configuration:', err);
            setError(err instanceof Error ? err.message : 'Failed to load system configuration');

            // Set default configuration on error
            const defaultConfig: SystemConfigDto = {
                apiSettings: {
                    baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5127',
                    timeout: 10000,
                    retryAttempts: 3,
                    enableCaching: true,
                    cacheTimeout: 300000
                },
                contentSettings: {
                    maxVocabularyPerLevel: 50,
                    maxGrammarLessonsPerLevel: 20,
                    maxDialoguesPerLevel: 15,
                    maxQuizzesPerLevel: 10,
                    defaultQuizTimeLimit: 300,
                    enableAudioFeatures: true
                },
                persianSettings: {
                    defaultFont: 'Vazirmatn',
                    enableRTL: true,
                    enableBidirectionalText: true,
                    persianNumberFormatting: true
                },
                securitySettings: {
                    tokenExpirationMinutes: 60,
                    sessionTimeoutMinutes: 60,
                    maxLoginAttempts: 5
                }
            };
            
            globalSystemConfig = defaultConfig;
            lastConfigLoad = Date.now();
            setSystemConfig(defaultConfig);
            await applySessionInactivityFromConfig(defaultConfig);
        } finally {
            setLoading(false);
            globalConfigPromise = null; // Clear the promise
        }
    };

    const refreshConfig = async () => {
        await loadSystemConfig();
    };

    useEffect(() => {
        loadSystemConfig();
    }, []);

    return {
        systemConfig,
        loading,
        error,
        refreshConfig
    };
};